import { AlertTriangle, CheckCircle, Edit, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PurchaseTransactionSummary } from '../types/purchaseTransaction';
import { formatCurrency } from '../utils/formatCurrency';

export type PurchaseSummaryCardStatus = 'open' | 'overdue' | 'draft' | 'paid';

interface SummaryCardConfig {
  key: PurchaseSummaryCardStatus;
  label: string;
  bucketKey: keyof Pick<PurchaseTransactionSummary, 'open' | 'overdue' | 'draft' | 'paid_recent'>;
  icon: LucideIcon;
  accent: string;
  text: string;
}

export interface PurchaseTransactionSummaryCardsProps {
  summary: PurchaseTransactionSummary | null;
  loading?: boolean;
  activeStatus?: PurchaseSummaryCardStatus | null;
  onCardClick?: (status: PurchaseSummaryCardStatus) => void;
  countLabel?: 'bill' | 'transaction';
  className?: string;
}

const PurchaseTransactionSummaryCards = ({
  summary,
  loading = false,
  activeStatus = null,
  onCardClick,
  countLabel = 'transaction',
  className = '',
}: PurchaseTransactionSummaryCardsProps) => {
  const cards: SummaryCardConfig[] = summary
    ? [
        {
          key: 'open',
          label: 'Open',
          bucketKey: 'open',
          icon: FileText,
          accent: 'border-amber-200 bg-amber-50',
          text: 'text-amber-900',
        },
        {
          key: 'overdue',
          label: 'Overdue',
          bucketKey: 'overdue',
          icon: AlertTriangle,
          accent: 'border-orange-200 bg-orange-50',
          text: 'text-orange-800',
        },
        {
          key: 'draft',
          label: 'Draft',
          bucketKey: 'draft',
          icon: Edit,
          accent: 'border-gray-200 bg-gray-50',
          text: 'text-gray-800',
        },
        {
          key: 'paid',
          label: `Paid (${summary.paid_recent.days ?? 30}d)`,
          bucketKey: 'paid_recent',
          icon: CheckCircle,
          accent: 'border-green-200 bg-green-50',
          text: 'text-green-800',
        },
      ]
    : [];

  if (loading) {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 ${className}`}>
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="p-4 rounded-xl border-2 border-gray-200 bg-white animate-pulse"
          >
            <div className="h-4 w-20 bg-gray-200 rounded mb-3" />
            <div className="h-8 w-28 bg-gray-200 rounded mb-2" />
            <div className="h-3 w-24 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const countWord = countLabel === 'bill' ? 'bill' : 'transaction';
  const countWordPlural = countLabel === 'bill' ? 'bills' : 'transactions';

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 ${className}`}>
      {cards.map((card) => {
        const Icon = card.icon;
        const bucket = summary[card.bucketKey];
        const active = activeStatus === card.key;
        const interactive = Boolean(onCardClick);

        return (
          <button
            key={card.key}
            type="button"
            onClick={onCardClick ? () => onCardClick(card.key) : undefined}
            disabled={!interactive}
            className={`text-left p-4 rounded-xl border-2 transition-all ${card.accent} ${
              interactive ? 'cursor-pointer' : 'cursor-default'
            } ${active ? 'ring-2 ring-slate-800 ring-offset-1' : interactive ? 'hover:shadow-sm' : ''}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-medium ${card.text}`}>{card.label}</span>
              <Icon className={`h-4 w-4 ${card.text}`} />
            </div>
            <p className={`text-2xl font-semibold ${card.text}`}>{formatCurrency(bucket.total)}</p>
            <p className="text-xs text-gray-500 mt-1">
              {bucket.count} {bucket.count === 1 ? countWord : countWordPlural}
            </p>
          </button>
        );
      })}
    </div>
  );
};

export default PurchaseTransactionSummaryCards;
